import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BetaSpotsCounter from '../components/BetaSpotsCounter';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [betaStatus, setBetaStatus] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  
  const { login, register, user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Get the intended destination from state (set by ProtectedRoute/AdminRoute)
  const from = location.state?.from?.pathname || '/dashboard';

  // Fetch beta status
  useEffect(() => {
    const fetchBetaStatus = async () => {
      try {
        const response = await axios.get(`${API}/beta/status`);
        setBetaStatus(response.data);
      } catch (error) {
        console.error('Error fetching beta status:', error);
      }
    };
    fetchBetaStatus();
  }, []);

  useEffect(() => {
    if (user) {
      // Redirect to intended destination or dashboard
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        const firstName = userData?.name?.split(' ')[0];
        toast.success(firstName ? t('auth.loginSuccessName', { name: firstName }) : t('auth.loginSuccess'));
        
        // Redirect to intended destination or dashboard
        window.location.href = from;
        return;
        
      } else {
        // Registration
        await register(formData.email, formData.password, formData.name);
        toast.success(t('auth.accountCreated'));
        
        // Force redirect after registration (always to dashboard for new users)
        window.location.href = "/dashboard";
        return;
      }
    } catch (error) {
      // Handle specific login errors with translated messages
      const errorDetail = error.response?.data?.detail;
      if (errorDetail === 'email_not_found') {
        toast.error(t('errors.emailNotFound'));
      } else if (errorDetail === 'invalid_password') {
        toast.error(t('errors.invalidPassword'));
      } else {
        toast.error(error.response?.data?.detail || t('errors.generic'));
      }
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    // Store the intended destination before redirecting to Google Auth
    if (from && from !== '/dashboard') {
      localStorage.setItem('auth_redirect_to', from);
    }
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Check if beta is full (disable registration)
  const isBetaFull = betaStatus?.is_full;

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-20 py-12 relative">
        {/* Background accent */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#D4AF37]/3 to-transparent pointer-events-none" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full mx-auto relative"
        >
          <Link to="/" className="group inline-block mb-12" data-testid="auth-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_brand-alchemy-2/artifacts/3hu8wfzt_24FA2A63-8890-4D16-8044-60FA948D15B0.png" 
              alt="Clierxter Vision Lab" 
              className="h-20 w-auto object-contain"
            />
          </Link>

          <div className="mb-10">
            <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
              {isLogin ? t('auth.welcomeBack') : t('auth.createAccount')}
            </h1>
            <p className="text-[#71717A] font-light">
              {isLogin 
                ? t('dashboard.welcomeMessage') 
                : t('auth.createAccount')}
            </p>
          </div>

          {/* Beta Spots Counter (only for signup) */}
          {!isLogin && (
            <div className="mb-8">
              <BetaSpotsCounter variant="default" showBenefits={true} />
            </div>
          )}

          {/* Note: Beta registration is now closed. Users must subscribe. */}

          {/* Google Auth Button */}
          <Button
            type="button"
            onClick={handleGoogleAuth}
            className="w-full bg-transparent border border-[#27272A] hover:border-[#D4AF37]/50 py-5 mb-8 flex items-center justify-center gap-3 text-white transition-all group"
            data-testid="google-auth-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#E5C585" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#D4AF37" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#A57A03" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#E5C585" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-sm tracking-wide group-hover:text-[#E5C585] transition-colors">{t('auth.continueWithGoogle')}</span>
          </Button>

          <div className="flex items-center gap-6 mb-8">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#27272A] to-transparent" />
            <span className="text-[#52525B] text-xs font-accent tracking-widest">{t('common.or').toUpperCase()}</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#27272A] to-transparent" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-[#71717A] text-xs font-accent tracking-wider mb-3 block">{t('auth.name').toUpperCase()}</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-luxury w-full"
                  placeholder={t('join.yourName')}
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="email" className="text-[#71717A] text-xs font-accent tracking-wider mb-3 block">{t('auth.email').toUpperCase()}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-luxury w-full"
                placeholder={t('join.yourEmail')}
                required
                data-testid="email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-[#71717A] text-xs font-accent tracking-wider mb-3 block">{t('auth.password').toUpperCase()}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-luxury w-full pr-10"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#E5C585] transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              
              {/* Forgot Password Link - Only show on login */}
              {isLogin && (
                <div className="mt-3 text-right">
                  <a
                    href="/forgot-password"
                    className="text-[#71717A] text-sm hover:text-[#E5C585] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </a>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || (!isLogin && isBetaFull)}
              className="w-full btn-gold py-5 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="animate-pulse">{t('common.loading')}</span>
              ) : (
                <>
                  {isLogin ? t('auth.signIn').toUpperCase() : t('auth.createAccount').toUpperCase()}
                  <ArrowRight size={18} />
                </>
              )}
            </Button>
          </form>

          <p className="text-center mt-10 text-[#71717A]">
            {isLogin ? t('auth.noAccount') + " " : t('auth.hasAccount') + " "}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#E5C585] hover:text-[#D4AF37] transition-colors font-medium"
              data-testid="toggle-auth-btn"
            >
              {isLogin ? t('auth.createAccount') : t('auth.signIn')}
            </button>
          </p>
        </motion.div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent z-10" />
        <img 
          src="https://customer-assets.emergentagent.com/job_brand-alchemy-2/artifacts/jt3ih9ok_IMG_2996.jpeg"
          alt="Brand Vision"
          className="w-full h-full object-cover object-top"
        />
        
        {/* Quote overlay */}
        <div className="absolute bottom-16 left-16 right-16 z-20">
          <div className="bg-[#050505]/80 backdrop-blur-xl p-8 border border-[#D4AF37]/20">
            <div className="text-[#D4AF37] font-serif text-4xl leading-none mb-3">"</div>
            <blockquote className="text-white text-xl font-serif italic leading-relaxed mb-4">
              Le moment où j'ai transformé ma vision en une identité de marque claire, tout a changé.
            </blockquote>
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-[#D4AF37]" />
              <p className="text-[#D4AF37] font-accent text-[10px] tracking-[0.2em]">UNE CLIENTE CLIERXTER</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
